/**
 * Template Gallery component for ClarCV application.
 * 
 * Displays available CV templates in a gallery format and allows
 * users to select a template to apply to their improved CV content.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.analysisData - Improved CV content from AI analysis
 * @param {Function} props.onTemplateSelect - Callback when template is selected
 * @param {Function} props.onClose - Callback to close the gallery
 * @param {boolean} [props.showAiTemplateOption=true] - If false, hides "Generate Template with AI" (e.g. when opened from edit-cv)
 * @returns {JSX.Element} Template gallery component
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useRequireAuth } from '../contexts/AuthGateContext';
import { useLanguage } from '../contexts/LanguageContext';
import { filenameFromAnalysisData } from '../utils/downloadFilename';
import { getTemplateDisplayName } from '../utils/templateNames';
import { usePlanLimitPrompt } from '../hooks/usePlanLimitPrompt';
import './TemplateGallery.css';

const mountGalleryOverlay = (node) => {
  if (typeof document === 'undefined' || !document.body) {
    return node;
  }
  return createPortal(node, document.body);
};

const resolveProductFlow = (analysisData, productFlow) => {
  if (productFlow === 'ats' || productFlow === 'ai_cv' || productFlow === 'from_scratch') {
    return productFlow;
  }
  return analysisData ? 'ai_cv' : 'from_scratch';
};

const TemplateGallery = ({
  analysisData,
  onTemplateSelect,
  onClose,
  showAiTemplateOption = true,
  cvLanguage = 'es',
  pickOnly = false,
  productFlow,
}) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const requireAuth = useRequireAuth();
  const { lang } = useLanguage();
  const { handlePlanLimitError, prompt: upgradePrompt } = usePlanLimitPrompt();
  const resolvedFlow = resolveProductFlow(analysisData, productFlow);
  // Never show "Generate Template with AI" on edit-cv page (prop or route)
  const showAi = showAiTemplateOption && !location.pathname.includes('/dashboard/edit-cv');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applying, setApplying] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'modern', 'classic', 'creative', 'minimal'
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [customizationPrompt, setCustomizationPrompt] = useState('');
  const [customizedHtml, setCustomizedHtml] = useState(null);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showAllTemplates, setShowAllTemplates] = useState(pickOnly);
  // Templates whose thumbnail failed to load fall back to the colour card.
  const [thumbnailErrors, setThumbnailErrors] = useState({});

  const INITIAL_TEMPLATES_SHOWN = 8;

  useEffect(() => {
    fetchTemplates();
  }, []);

  const closeButtonRef = useRef(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (showAiModal) {
          setShowAiModal(false);
        } else if (onClose) {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, showAiModal]);

  // Cleanup: revoke object URL when component unmounts or previewUrl changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  /**
   * Fetch available CV templates from the API.
   */
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/cvs/templates/');
      console.log('Templates fetched:', response.data.length, 'templates');
      console.log('Templates data:', response.data);
      setTemplates(response.data);
      
      if (response.data.length === 0) {
        toast.warning('No hay plantillas disponibles. Contacta con soporte.');
      }
      } catch (error) {
        console.error('Error fetching templates:', error);
        if (error.response?.status !== 401 && error.response?.status !== 403) {
          toast.error('Error al cargar plantillas: ' + (error.response?.data?.detail || error.message));
        }
      } finally {
      setLoading(false);
    }
  };

  /**
   * Handle template selection.
   * 
   * @param {Object} template - Selected template object
   */
  const handleTemplateSelect = (template) => {
    // Clean up previous preview URL
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    
    // Reset customization when selecting new template
    setCustomizedHtml(null);
    setCustomizationPrompt('');
    
    setSelectedTemplate(template);
    if (pickOnly) {
      if (onTemplateSelect) {
        onTemplateSelect(template);
      }
      if (onClose) {
        onClose();
      }
      return;
    }
    generatePreview(template);
  };

  /**
   * Generate preview with custom HTML template.
   * 
   * @param {string} customHtml - Custom HTML template to use
   */
  const generatePreviewWithCustomHtml = async (customHtml) => {
    if (!analysisData || !isAuthenticated) {
      return;
    }

    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      // Extract photo from analysisData
      let photoFile = null;
      const finalDesign = analysisData.final_design || {};
      const extractedImproved = analysisData.extracted_and_improved || {};
      const editableContent = analysisData.editableContent || {};
      
      const photo = analysisData.photo || 
                    finalDesign.photo || 
                    extractedImproved.photo || 
                    editableContent.photo;
      
      if (photo && photo instanceof File) {
        photoFile = photo;
      }

      // Prepare request with custom HTML
      const formData = new FormData();
      formData.append('analysis_data', JSON.stringify(analysisData));
      formData.append('custom_html', customHtml);
      formData.append('output_format', 'html');
      formData.append('preview', 'true');
      formData.append('cv_language', cvLanguage);
      formData.append('product_flow', resolvedFlow);
      
      if (photoFile) {
        formData.append('photo', photoFile);
      }

      const response = await axios.post(
        '/api/cvs/apply-template/',
        formData,
        { responseType: 'text' }
      );

      const htmlBlob = new Blob([response.data], { type: 'text/html; charset=utf-8' });
      const url = window.URL.createObjectURL(htmlBlob);
      
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
      
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error generating preview with custom HTML:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al generar la vista previa';
      toast.error(`Error de vista previa: ${errorMessage}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  /**
   * Handle template customization with AI.
   */
  const handleCustomize = async () => {
    if (!customizationPrompt.trim()) {
      toast.warning('Describe cómo quieres que sea la plantilla de tu CV');
      return;
    }
    if (!(await requireAuth())) {
      return;
    }

    setIsCustomizing(true);
    try {
      const response = await axios.post('/api/cvs/customize-template/', {
        prompt: customizationPrompt
      });

      // Update customized HTML (this is the complete template HTML)
      setCustomizedHtml(response.data.html);

      // Generate preview with the new template HTML filled with user data
      if (analysisData) {
        await generatePreviewWithCustomHtml(response.data.html);
      } else {
        // Just show the template structure if no data
        const htmlBlob = new Blob([response.data.html], { type: 'text/html; charset=utf-8' });
        const url = window.URL.createObjectURL(htmlBlob);
        
        if (previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
        }
        
        setPreviewUrl(url);
      }
      
      toast.success('Plantilla generada correctamente');
      setShowAiModal(false);
    } catch (error) {
      console.error('Error generating template:', error);
      if (await handlePlanLimitError(error)) {
        return;
      }
      const errorMessage = error.response?.data?.error || error.message || 'Error al generar la plantilla';
      toast.error(errorMessage);
    } finally {
      setIsCustomizing(false);
    }
  };

  /**
   * Generate preview of template with user's data.
   * 
   * @param {Object} template - Template to preview
   */
  const generatePreview = async (template) => {
    if (!analysisData || !template || !isAuthenticated) {
      return;
    }

    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      // For HTML templates, request HTML directly (better rendering)
      // For other templates, request PDF
      const isHtmlTemplate = template.template_type === 'html';
      
      // Extract photo from analysisData if it exists
      // Photo can be in analysisData.photo, analysisData.final_design.photo, 
      // analysisData.extracted_and_improved.photo, or editableContent.photo
      let photoFile = null;
      const finalDesign = analysisData.final_design || {};
      const extractedImproved = analysisData.extracted_and_improved || {};
      const editableContent = analysisData.editableContent || {};
      
      // Try to find photo in various locations
      const photo = analysisData.photo || 
                    finalDesign.photo || 
                    extractedImproved.photo || 
                    editableContent.photo;
      
      console.log('🔍 DEBUG PHOTO - Step 1: Finding photo');
      console.log('  - analysisData.photo:', analysisData.photo);
      console.log('  - finalDesign.photo:', finalDesign.photo);
      console.log('  - extractedImproved.photo:', extractedImproved.photo);
      console.log('  - editableContent.photo:', editableContent.photo);
      console.log('  - Found photo:', photo);
      console.log('  - Photo type:', typeof photo);
      console.log('  - Is File?', photo instanceof File);
      
      // If photo is a File object, extract it
      if (photo && photo instanceof File) {
        console.log('✅ Photo is a File object, using directly');
        photoFile = photo;
      } else if (photo && typeof photo === 'string') {
        console.log('📝 Photo is a string, attempting to convert to File');
        console.log('  - Photo string value:', photo);
        // If photo is a string (path or URL), try to fetch it and convert to File
        // This handles cases where photo was selected in edit-cv but is stored as path
        try {
          // Check if it's a valid URL (http/https) or data URL
          if (photo.startsWith('http://') || photo.startsWith('https://') || photo.startsWith('data:')) {
            // It's a URL, try to fetch it
            const response = await fetch(photo);
            if (response.ok) {
              const blob = await response.blob();
              // Create File from blob
              const fileName = photo.split('/').pop() || 'photo.jpg';
              photoFile = new File([blob], fileName, { type: blob.type });
            }
          } else if (photo.startsWith('/media/')) {
            // It's a backend media path, construct full URL
            const baseURL = axios.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const fullUrl = `${baseURL}${photo}`;
            console.log('  - Backend media path detected, fetching from:', fullUrl);
            const response = await fetch(fullUrl);
            if (response.ok) {
              const blob = await response.blob();
              const fileName = photo.split('/').pop() || 'photo.jpg';
              photoFile = new File([blob], fileName, { type: blob.type });
              console.log('✅ Successfully converted backend path to File:', fileName);
            } else {
              console.warn('  - Failed to fetch photo from backend:', response.status);
            }
          } else {
            console.log('  - Photo string does not match URL or /media/ pattern');
            console.log('  - Will include as photo_path in analysis_data');
          }
        } catch (error) {
          console.error('❌ Could not convert photo string to File:', error);
          // If conversion fails, include photo_path in analysis_data for backend to handle
          if (!analysisData.photo_path) {
            analysisData.photo_path = photo;
            console.log('  - Added photo_path to analysis_data:', photo);
          }
        }
      }
      
      console.log('🔍 DEBUG PHOTO - Step 2: Final photoFile status');
      console.log('  - photoFile:', photoFile);
      console.log('  - photoFile type:', photoFile ? typeof photoFile : 'null');
      
      // Prepare request data
      let requestData;
      let config;
      
      if (photoFile) {
        console.log('📤 Using FormData with photo file');
        // Use FormData if we have a photo file
        const formData = new FormData();
        formData.append('analysis_data', JSON.stringify(analysisData));
        formData.append('template_id', template.id);
        formData.append('output_format', isHtmlTemplate ? 'html' : 'pdf');
        formData.append('preview', 'true');
        formData.append('cv_language', cvLanguage);
        formData.append('product_flow', resolvedFlow);
        formData.append('photo', photoFile);
        console.log('  - FormData created with photo file:', photoFile.name, photoFile.size, 'bytes');
        
        requestData = formData;
        config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: isHtmlTemplate ? 'text' : 'blob',
        };
      } else {
        console.log('📤 Using JSON payload (no photo file)');
        // Use JSON if no photo file, but include photo_path if available
        const photoPathToInclude = analysisData.photo_path || (typeof photo === 'string' && !photo.startsWith('http') && !photo.startsWith('data:') ? photo : null);
        console.log('  - Including photo_path in analysis_data:', photoPathToInclude);
        const requestPayload = {
          analysis_data: {
            ...analysisData,
            photo_path: photoPathToInclude
          },
          template_id: template.id,
          output_format: isHtmlTemplate ? 'html' : 'pdf',
          preview: true,
          cv_language: cvLanguage,
          product_flow: resolvedFlow,
        };
        requestData = requestPayload;
        config = {
          responseType: isHtmlTemplate ? 'text' : 'blob',
        };
      }
      
      console.log('🔍 DEBUG PHOTO - Step 3: Request prepared');
      console.log('  - Request type:', photoFile ? 'FormData' : 'JSON');
      console.log('  - Template ID:', template.id);
      console.log('  - Output format:', isHtmlTemplate ? 'html' : 'pdf');
      
      const response = await axios.post(
        '/api/cvs/apply-template/',
        requestData,
        config
      );

      if (isHtmlTemplate) {
        // For HTML templates, create a blob with HTML content
        const htmlContent = response.data;
        console.log('='.repeat(80));
        console.log('🔍 DEBUG PHOTO - Frontend: HTML received from backend');
        console.log('  - HTML length:', htmlContent.length, 'chars');
        console.log('  - Contains "data:image":', htmlContent.includes('data:image'));
        console.log('  - Contains "base64":', htmlContent.includes('base64'));
        console.log('  - Contains "<img":', htmlContent.includes('<img'));
        
        // Check if photo is embedded
        const photoMatch = htmlContent.match(/data:image\/[^;]+;base64,[^"'\s]+/);
        if (photoMatch) {
          console.log('  - ✅ Photo data URI found!');
          console.log('  - Data URI length:', photoMatch[0].length, 'chars');
          console.log('  - Data URI preview (first 100 chars):', photoMatch[0].substring(0, 100));
          console.log('  - Data URI preview (last 50 chars):', '...' + photoMatch[0].substring(photoMatch[0].length - 50));
        } else {
          console.warn('  - ❌ No photo data URI found in HTML!');
        }
        
        // Check for img tags
        const imgTags = htmlContent.match(/<img[^>]*>/g);
        if (imgTags) {
          console.log('  - Number of <img> tags found:', imgTags.length);
          imgTags.forEach((tag, idx) => {
            console.log(`  - Image tag ${idx + 1}:`, tag.substring(0, 200));
            if (tag.includes('data:image')) {
              console.log(`    ✅ Contains data:image`);
            } else {
              console.log(`    ⚠️ Does NOT contain data:image`);
            }
          });
        } else {
          console.warn('  - ⚠️ No <img> tags found in HTML!');
        }
        
        // Check for unreplaced placeholders
        const remainingPlaceholders = htmlContent.match(/\{\{photo\}\}/g);
        if (remainingPlaceholders) {
          console.warn(`  - ⚠️ WARNING: Found ${remainingPlaceholders.length} unreplaced {{photo}} placeholders!`);
        } else {
          console.log('  - ✅ No unreplaced {{photo}} placeholders');
        }
        
        // Check for file paths that might be displayed as text
        const filePathPattern = /[A-Za-z]:[/\\][^\s<>"]*\.(jpg|jpeg|png|gif|bmp)/gi;
        const filePaths = htmlContent.match(filePathPattern);
        if (filePaths) {
          console.warn(`  - ⚠️ WARNING: Found ${filePaths.length} file paths in HTML (may appear as text)`);
          filePaths.slice(0, 3).forEach((path, idx) => {
            console.warn(`    - Path ${idx + 1}: ${path}`);
          });
        } else {
          console.log('  - ✅ No file paths found in HTML');
        }
        
        const htmlBlob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
        const url = window.URL.createObjectURL(htmlBlob);
        console.log('  - Blob URL created:', url);
        console.log('  - Blob size:', htmlBlob.size, 'bytes');
        console.log('='.repeat(80));
        setPreviewUrl(url);
      } else {
        // For PDF templates, verify it's a PDF
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // Verify it's a PDF by checking the first bytes
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const isPDF = uint8Array.length >= 4 && 
                      uint8Array[0] === 0x25 && // %
                      uint8Array[1] === 0x50 && // P
                      uint8Array[2] === 0x44 && // D
                      uint8Array[3] === 0x46;   // F
        
        if (isPDF) {
          const url = window.URL.createObjectURL(blob);
          setPreviewUrl(url);
        } else {
          // If not PDF, show error
          console.error('Preview response is not a PDF');
          toast.error('Error al generar la vista previa PDF. El documento puede no estar disponible.');
        }
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al generar la vista previa';
      toast.error(`Error de vista previa: ${errorMessage}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  /**
   * Apply selected template to improved CV content.
   * 
   * @param {string} outputFormat - Output format ('docx' or 'pdf')
   */
  const applyTemplate = async (outputFormat = 'docx') => {
    // Allow applying template even if no template is selected, if we have customized HTML
    if (!selectedTemplate && !customizedHtml) {
      toast.error('Elige primero una plantilla o genera una con IA');
      return;
    }

    if (!analysisData) {
      toast.error('No hay datos del CV');
      return;
    }
    if (!(await requireAuth())) {
      return;
    }

    setApplying(true);

    try {
      // Extract photo from analysisData if it exists
      let photoFile = null;
      const finalDesign = analysisData.final_design || {};
      const extractedImproved = analysisData.extracted_and_improved || {};
      const editableContent = analysisData.editableContent || {};
      
      const photo = analysisData.photo || 
                    finalDesign.photo || 
                    extractedImproved.photo || 
                    editableContent.photo;
      
      // If photo is a File object, extract it
      if (photo && photo instanceof File) {
        photoFile = photo;
      } else if (photo && typeof photo === 'string') {
        // If photo is a string (path or URL), try to fetch it and convert to File
        try {
          // Check if it's a valid URL (http/https) or data URL
          if (photo.startsWith('http://') || photo.startsWith('https://') || photo.startsWith('data:')) {
            // It's a URL, try to fetch it
            const response = await fetch(photo);
            if (response.ok) {
              const blob = await response.blob();
              const fileName = photo.split('/').pop() || 'photo.jpg';
              photoFile = new File([blob], fileName, { type: blob.type });
            }
          } else if (photo.startsWith('/media/')) {
            // It's a backend media path, construct full URL
            const baseURL = axios.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const fullUrl = `${baseURL}${photo}`;
            const response = await fetch(fullUrl);
            if (response.ok) {
              const blob = await response.blob();
              const fileName = photo.split('/').pop() || 'photo.jpg';
              photoFile = new File([blob], fileName, { type: blob.type });
            }
          }
        } catch (error) {
          console.warn('Could not convert photo string to File:', error);
        }
      }
      
      // Prepare request data
      let requestData;
      let config;
      
      // Check if we should use custom HTML
      const useCustomHtml = customizedHtml !== null;
      
      if (photoFile || useCustomHtml) {
        // Use FormData if we have a photo file or custom HTML
        const formData = new FormData();
        formData.append('analysis_data', JSON.stringify(analysisData));
        
        if (useCustomHtml) {
          formData.append('custom_html', customizedHtml);
        } else {
          formData.append('template_id', selectedTemplate.id);
        }
        
        formData.append('output_format', outputFormat);
        formData.append('cv_language', cvLanguage);
        formData.append('product_flow', resolvedFlow);
        formData.append('fit_to_one_page', 'true');
        if (photoFile) {
          formData.append('photo', photoFile);
        }
        
        requestData = formData;
        config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'blob',
        };
      } else {
        // Use JSON if no photo file and no custom HTML
        const requestPayload = {
          analysis_data: {
            ...analysisData,
            photo_path: analysisData.photo_path || (typeof photo === 'string' && !photo.startsWith('http') && !photo.startsWith('data:') ? photo : null)
          },
          template_id: selectedTemplate.id,
          output_format: outputFormat,
          cv_language: cvLanguage,
          product_flow: resolvedFlow,
          fit_to_one_page: true,
        };
        requestData = requestPayload;
        config = {
          responseType: 'blob',
        };
      }
      
      const response = await axios.post(
        '/api/cvs/apply-template/',
        requestData,
        config
      );

      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        throw new Error('Archivo vacío recibido del servidor');
      }

      // Check if response is an error (JSON instead of blob)
      // Sometimes errors come as JSON blobs - clone blob before reading
      if (response.data.type && response.data.type.includes('application/json')) {
        // Clone blob before reading (blobs can only be read once)
        const clonedBlob = response.data.slice();
        const text = await clonedBlob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.detail || 'Error al generar el CV');
        } catch (parseError) {
          // If parsing fails, it might not be JSON
          if (text.includes('error') || text.includes('Error')) {
            throw new Error('Error al generar el CV: ' + text.substring(0, 200));
          }
          throw new Error('Error al generar el CV');
        }
      }

      // Verify blob size is reasonable (at least 100 bytes for PDF/DOCX)
      // Very small files might be error messages - clone blob before reading
      if (response.data.size < 100) {
        // Clone blob before reading (blobs can only be read once)
        const clonedBlob = response.data.slice();
        const text = await clonedBlob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.detail || 'Error al generar el CV');
        } catch (parseError) {
          // If it's not JSON and contains error keywords, it's likely an error
          if (text.toLowerCase().includes('error') || text.toLowerCase().includes('failed')) {
            throw new Error('Error al generar el CV: ' + text.substring(0, 200));
          }
          // Otherwise, proceed with small file (might be valid)
        }
      }

      // Create download link with correct MIME type
      const mimeTypes = {
        'pdf': 'application/pdf',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      };
      const blob = new Blob([response.data], { type: mimeTypes[outputFormat] || 'application/octet-stream' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      link.download = filenameFromAnalysisData(analysisData, outputFormat);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      const templateDisplayName = customizedHtml ? 'Plantilla generada con IA' : (selectedTemplate?.name || 'Plantilla');
      toast.success(`CV generado con ${templateDisplayName}`);
      
      if (onTemplateSelect && selectedTemplate) {
        onTemplateSelect(selectedTemplate);
      }
    } catch (error) {
      console.error('Error applying template:', error);
      if (await handlePlanLimitError(error)) {
        return;
      }
      
      // Try to extract error message from blob response if it's JSON
      let errorMessage = 'Error al aplicar la plantilla';
      
      if (error.response && error.response.data instanceof Blob) {
        try {
          // Clone the blob before reading (blobs can only be read once)
          const text = await error.response.data.text();
          try {
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorData.detail || errorMessage;
            if (error.response?.status === 403 && errorData.upgrade_url) {
              errorMessage += ' Ve a Precios para mejorar a Pro.';
            }
          } catch (e) {
            // Not JSON, check if it contains error text
            if (text.toLowerCase().includes('error') || text.toLowerCase().includes('failed')) {
              errorMessage = text.substring(0, 200);
            } else {
              errorMessage = error.message || errorMessage;
            }
          }
        } catch (e) {
          // Can't read blob, use default message
          errorMessage = error.message || errorMessage;
        }
      } else if (error.response?.status === 429 || error.response?.data?.error_code === 'rate_limited') {
        errorMessage =
          error.response?.data?.error ||
          'Demasiadas plantillas seguidas. Espera unos segundos e inténtalo de nuevo.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      // Always reset applying state, even if there was an error
      setApplying(false);
    }
  };

  /**
   * Filter templates by category.
   */
  const filteredTemplates = filter === 'all' 
    ? templates 
    : templates.filter(t => t.category === filter);

  const CATEGORY_LABELS = {
    all: 'Todas',
    modern: 'Moderno',
    classic: 'Clásico',
    creative: 'Creativo',
    minimal: 'Minimalista',
    tech: 'Tecnología',
  };

  const categoryLabel = (category) => CATEGORY_LABELS[category] || category;
  const visibleTemplates = pickOnly || showAllTemplates
    ? filteredTemplates
    : filteredTemplates.slice(0, INITIAL_TEMPLATES_SHOWN);
  const hasMoreTemplates = !pickOnly && filteredTemplates.length > INITIAL_TEMPLATES_SHOWN;

  useEffect(() => {
    setShowAllTemplates(pickOnly);
  }, [filter, pickOnly]);

  // Debug: Log filtered templates
  useEffect(() => {
    if (templates.length > 0) {
      console.log('Templates loaded:', templates.length);
      console.log('Filtered templates:', filteredTemplates.length, 'for filter:', filter);
    }
  }, [templates, filteredTemplates, filter]);

  if (loading) {
    return mountGalleryOverlay(
      <div className="template-gallery-overlay">
        <div className="template-gallery">
          <div className="loading-container">
            <div className="spinner" />
            <p>Cargando plantillas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {mountGalleryOverlay(
    <div
      className="template-gallery-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`template-gallery${pickOnly ? ' template-gallery--pick' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-gallery-title"
      >
        <div className="template-gallery-header">
          <h2 id="template-gallery-title">Elige tu plantilla de CV</h2>
          <button
            type="button"
            className="close-button"
            onClick={onClose}
            aria-label="Cerrar galería de plantillas"
            ref={closeButtonRef}
          >
            ×
          </button>
        </div>

        <div className="template-gallery-content">
          {/* Left Panel: Template Selection */}
          <div className="template-selection-panel">
            {/* Filters with count */}
            <div className="template-filters-header">
              <div className="template-gallery-filters">
                <button
                  className={`filter-button ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  Todas
                  <span className="filter-count">{templates.length}</span>
                </button>
                <button
                  className={`filter-button ${filter === 'modern' ? 'active' : ''}`}
                  onClick={() => setFilter('modern')}
                >
                  Moderno
                  <span className="filter-count">{templates.filter(t => t.category === 'modern').length}</span>
                </button>
                <button
                  className={`filter-button ${filter === 'classic' ? 'active' : ''}`}
                  onClick={() => setFilter('classic')}
                >
                  Clásico
                  <span className="filter-count">{templates.filter(t => t.category === 'classic').length}</span>
                </button>
                <button
                  className={`filter-button ${filter === 'creative' ? 'active' : ''}`}
                  onClick={() => setFilter('creative')}
                >
                  Creativo
                  <span className="filter-count">{templates.filter(t => t.category === 'creative').length}</span>
                </button>
                <button
                  className={`filter-button ${filter === 'minimal' ? 'active' : ''}`}
                  onClick={() => setFilter('minimal')}
                >
                  Minimalista
                  <span className="filter-count">{templates.filter(t => t.category === 'minimal').length}</span>
                </button>
              </div>
              <div className="templates-showing">
                {pickOnly
                  ? <>Elige un look. Se aplica al instante en tu CV.</>
                  : hasMoreTemplates && !showAllTemplates
                    ? <>Mostrando <strong>{visibleTemplates.length}</strong> de <strong>{filteredTemplates.length}</strong> plantillas</>
                    : <>Mostrando <strong>{filteredTemplates.length}</strong> plantillas</>
                }
              </div>
            </div>

            {/* Template Carousel */}
            <div className="template-carousel-container">
              <div className="template-grid">
              {filteredTemplates.length === 0 ? (
                <div className="no-templates">
                  <p>No hay plantillas en esta categoría.</p>
                  <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                    Prueba con "Todas" para ver todas las plantillas.
                  </p>
                </div>
              ) : (
                visibleTemplates.map((template) => {
                  // Color schemes for each template
                  const templateColors = {
                    // Modern
                    'Premium Dark': { bg: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', accent: '#8b5cf6' },
                    'Futuristic Tech': { bg: 'linear-gradient(135deg, #0a0a0a 0%, #001a0f 100%)', accent: '#00ff88' },
                    'Tokyo Night': { bg: 'linear-gradient(135deg, #0f0f1a 0%, #1a0a2e 100%)', accent: '#ff0080' },
                    'Silicon Valley': { bg: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', accent: '#3b82f6' },
                    'Gradient Flow': { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', accent: '#f093fb' },
                    // Classic
                    'Elegant Classic': { bg: 'linear-gradient(135deg, #f5f3ef 0%, #e8e0d5 100%)', accent: '#8b7355', textDark: true },
                    'Luxury Executive': { bg: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', accent: '#d4af37' },
                    'Parisian Chic': { bg: 'linear-gradient(135deg, #fffef9 0%, #f5f3ef 100%)', accent: '#8b7355', textDark: true },
                    'Art Deco Luxe': { bg: 'linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%)', accent: '#c9a227' },
                    'Oxford Academic': { bg: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)', accent: '#c5a572' },
                    // Creative
                    'Creative Modernist': { bg: 'linear-gradient(135deg, #2d3436 0%, #1e272e 100%)', accent: '#e74c3c' },
                    'Swiss Design': { bg: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)', accent: '#ff0000', textDark: true },
                    'Portfolio Artist': { bg: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', accent: '#e94560' },
                    'Bold Statement': { bg: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)', accent: '#ff6b35' },
                    // Minimal
                    'Minimalist Luxury': { bg: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)', accent: '#1a1a1a', textDark: true },
                    'Nordic Minimal': { bg: 'linear-gradient(135deg, #f7f7f5 0%, #e8e8e6 100%)', accent: '#1a1a1a', textDark: true },
                    'Paper White': { bg: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)', accent: '#2c2c2c', textDark: true },
                    'Mono Space': { bg: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)', accent: '#1a1a1a', textDark: true },
                  };
                  const colors = templateColors[template.name] || { bg: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', accent: '#ffffff' };
                  const displayName = getTemplateDisplayName(template.name, lang);

                  
                  return (
                    <div
                      key={template.id}
                      className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''} ${template.is_premium ? 'premium' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedTemplate?.id === template.id}
                      aria-label={`Plantilla ${displayName}${template.is_premium ? ', Premium' : ''}`}
                      onClick={() => handleTemplateSelect(template)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleTemplateSelect(template);
                        }
                      }}
                    >
                      {template.is_premium && (
                        <div className="premium-badge">Premium</div>
                      )}
                      {template.thumbnail_url && !thumbnailErrors[template.id] ? (
                        <div className="template-preview-card template-preview-card--shot">
                          <img
                            className="template-thumb"
                            src={template.thumbnail_url}
                            alt={`Vista previa de la plantilla ${displayName}`}
                            loading="lazy"
                            decoding="async"
                            onError={() =>
                              setThumbnailErrors((prev) => ({ ...prev, [template.id]: true }))
                            }
                          />
                        </div>
                      ) : (
                        <div
                          className="template-preview-card"
                          style={{ background: colors.bg }}
                        >
                          <div className="template-preview-content">
                            <div
                              className="template-preview-name"
                              style={{ color: colors.textDark ? '#1a1a1a' : '#ffffff' }}
                            >
                              {displayName}
                            </div>
                            <div
                              className="template-preview-accent"
                              style={{ background: colors.accent }}
                            />
                            <div
                              className="template-preview-category"
                              style={{
                                color: colors.textDark ? '#666' : 'rgba(255,255,255,0.7)',
                                borderColor: colors.textDark ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'
                              }}
                            >
                              {categoryLabel(template.category)}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="template-info">
                        <h3>{displayName}</h3>
                        <p>{template.description}</p>
                      </div>
                    </div>
                  );
                })
              )}
              {hasMoreTemplates && !pickOnly && (
                <div className="template-show-more-wrap" style={{ gridColumn: '1 / -1', textAlign: 'center', marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="filter-button"
                    onClick={() => setShowAllTemplates((prev) => !prev)}
                    style={{ padding: '10px 24px' }}
                  >
                    {showAllTemplates ? 'Ver menos' : `Ver más (${filteredTemplates.length - INITIAL_TEMPLATES_SHOWN} más)`}
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>

          {!pickOnly && (selectedTemplate || customizedHtml) && (
            <div className="template-preview-section">
              <div className="template-preview-container">
                <div className="preview-header">
                  <h3>Vista previa: {customizedHtml ? 'Plantilla generada con IA' : (selectedTemplate?.name || 'Plantilla')}</h3>
                  <p>Así se verá tu CV con esta plantilla</p>
                </div>
                
              {previewLoading ? (
                <div className="preview-loading">
                  <div className="spinner" />
                  <p>Generando vista previa...</p>
                </div>
              ) : previewUrl ? (
                <div className="preview-content">
                  <iframe
                    src={previewUrl}
                    className="preview-iframe"
                    title="Vista previa de plantilla"
                    sandbox="allow-same-origin allow-scripts"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      backgroundColor: '#fff'
                    }}
                    onLoad={() => {
                      console.log('🔍 DEBUG PHOTO - iframe loaded');
                      const iframe = document.querySelector('.preview-iframe');
                      if (iframe && iframe.contentDocument) {
                        const imgElements = iframe.contentDocument.querySelectorAll('img');
                        console.log('  - Images found in iframe:', imgElements.length);
                        imgElements.forEach((img, idx) => {
                          console.log(`  - Image ${idx + 1}:`, {
                            src: img.src.substring(0, 100) + '...',
                            complete: img.complete,
                            naturalWidth: img.naturalWidth,
                            naturalHeight: img.naturalHeight
                          });
                          img.onerror = () => {
                            console.error(`  - ❌ Image ${idx + 1} failed to load:`, img.src.substring(0, 100));
                          };
                          img.onload = () => {
                            console.log(`  - ✅ Image ${idx + 1} loaded successfully`);
                          };
                        });
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="preview-placeholder">
                  <p>Elige una plantilla para ver la vista previa</p>
                </div>
              )}
              </div>

              {/* Button to open AI Template modal - hidden when gallery is used from edit-cv */}
              {showAi && (
                <div className="ai-template-trigger">
                  <button
                    type="button"
                    className="btn btn-ai-template"
                    onClick={() => setShowAiModal(true)}
                  >
                    🎨 Generar plantilla con IA
                  </button>
                  {customizedHtml && (
                    <span className="customized-badge-inline">✨ Plantilla IA activa</span>
                  )}
                </div>
              )}

              {!pickOnly ? (
              <div className="template-actions">
                <div className="selected-template-info">
                  <strong>Seleccionada:</strong> {customizedHtml ? 'Plantilla generada con IA' : (selectedTemplate?.name || 'Ninguna')}
                  {showAi && customizedHtml && (
                    <>
                      <span className="customized-badge">✨ Generada con IA</span>
                      <button
                        type="button"
                        className="btn-link reset-ai-link"
                        onClick={() => {
                          setCustomizedHtml(null);
                          setCustomizationPrompt('');
                          if (selectedTemplate) {
                            generatePreview(selectedTemplate);
                          } else {
                            setPreviewUrl(null);
                          }
                        }}
                      >
                        Restablecer
                      </button>
                    </>
                  )}
                </div>
                <div className="action-buttons">
                  <button
                    className="btn btn-primary"
                    onClick={() => applyTemplate('pdf')}
                    disabled={applying || (!selectedTemplate && !customizedHtml)}
                  >
                    {applying ? 'Generando...' : 'Descargar PDF'}
                  </button>
                </div>
              </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* AI Generate Template Modal - separate window so it doesn't cover the preview */}
      {showAi && showAiModal && (
        <div className="ai-template-modal-overlay" onClick={() => setShowAiModal(false)}>
          <div className="ai-template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-template-modal-header">
              <h3>🎨 Generar plantilla con IA</h3>
              <button type="button" className="ai-template-modal-close" onClick={() => setShowAiModal(false)} aria-label="Cerrar">×</button>
            </div>
            <p className="ai-template-modal-desc">Describe cómo quieres que se vea la plantilla de tu CV (colores, estilo, tipografías, diseño, etc.)</p>
            <textarea
              className="ai-template-modal-textarea"
              value={customizationPrompt}
              onChange={(e) => setCustomizationPrompt(e.target.value)}
              placeholder="Ej.: Colores azul oscuro, estilo minimalista, tipografía sans-serif moderna, espaciado elegante, diseño profesional con barra lateral..."
              rows={4}
              disabled={isCustomizing}
            />
            <div className="ai-template-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAiModal(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCustomize}
                disabled={isCustomizing || !customizationPrompt.trim()}
              >
                {isCustomizing ? 'Generando...' : '✨ Generar plantilla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      )}
      {upgradePrompt}
    </>
  );
};

export default TemplateGallery;

